import type { VoidComponent } from "solid-js";
import { createEffect, createSignal, Show, untrack } from "solid-js";
import { makeEventListener } from "@solid-primitives/event-listener";
import SubformShowEvent from "../../../events/SubformShowEvent";
import SubformService from "../../../services/SubformService";
import type { Subform } from "../../../state/formVersion";
import Drawer, { CLOSING_ANIMATION_DURATION_IN_MILLISECONDS } from "../../../components/drawer/Drawer";
import SubformEditor from "./SubformEditor";
import { isNil } from "lodash-es";
import type { SubformEditorTab } from "./types";
import posthog from "posthog-js";
import { POSTHOG_EVENTS } from "../../../services/PostHogService";
import { useTranslation } from "../../../state/i18n";

const DELAY_TOLERANCE_IN_MS = 50;

const SubformDrawer: VoidComponent = () => {
  const { t } = useTranslation();
  const [editDrawerOpen, setEditDrawerOpen] = createSignal<{ subform: Subform; tab: SubformEditorTab }>();

  makeEventListener<Record<string, SubformShowEvent>>(document, SubformShowEvent.name, (event) => {
    const isDrawerClosed = untrack(() => isNil(editDrawerOpen()));
    const isDrawerOpenWithRequestedSubform = untrack(() => event.subformId.equals(editDrawerOpen()?.subform.uid));
    const subform = SubformService.getSubform(event.subformId);
    if (isNil(subform)) {
      return;
    }

    if (isDrawerClosed || isDrawerOpenWithRequestedSubform) {
      setEditDrawerOpen({ subform, tab: event.tab });
    } else {
      // Close drawer before re-opening so we have an animation to make it clear the user is opening another subform
      setEditDrawerOpen(undefined);
      // We need to wait until the closing animation is complete before we can re-open the drawer
      setTimeout(
        () => setEditDrawerOpen({ subform, tab: event.tab }),
        CLOSING_ANIMATION_DURATION_IN_MILLISECONDS + DELAY_TOLERANCE_IN_MS,
      );
    }
  });

  createEffect(() => {
    if (!isNil(editDrawerOpen())) {
      posthog.capture(POSTHOG_EVENTS.SUBFORM_OPENED, { $set: { subform_id: editDrawerOpen()?.subform.uid } });
    }
  });

  return (
    <Drawer
      title={t(($) => $.SUBFORMS_EDITOR_DRAWER_TITLE, { name: editDrawerOpen()?.subform.meta.name })}
      open={!isNil(editDrawerOpen())}
      setOpen={() => {
        setEditDrawerOpen(undefined);
      }}
    >
      <Show when={editDrawerOpen()}>
        {(subform) => (
          <SubformEditor
            subform={subform().subform}
            tab={subform().tab}
            onTabChange={(tab) => {
              setEditDrawerOpen({ subform: subform().subform, tab });
            }}
          />
        )}
      </Show>
    </Drawer>
  );
};

export default SubformDrawer;
